'use client';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import { CmsIcon } from '@eliadmin/core/src/components/nt/ui/cms-image';
import { useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Shared dropdown surface for finder fields, forms and sorting. */
export function SelectPopover({
  label,
  trigger,
  children,
  align = 'start',
  onOpenChange,
}: {
  label: string;
  trigger: ReactElement;
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        collisionPadding={16}
        aria-label={label}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] min-w-[min(320px,calc(100vw-32px))] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-xl border-[#E2E5DF] bg-white p-3 text-[#0D110D] shadow-float"
      >
        {children(() => changeOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

export const SELECT_FIELD =
  'nt-body-m h-11 w-full min-w-0 rounded-xl border border-[#E2E5DF] bg-white px-3 text-[#0D110D] focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-[#F5F6F3] disabled:text-[#8F978E]';

export function FinderSelect({
  label,
  value,
  image,
  disabled,
  className,
  children,
}: {
  label: string;
  value: string;
  image?: ImageType | null;
  disabled?: boolean;
  className?: string;
  children: (close: () => void) => ReactNode;
}) {
  const labelId = useId();
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <span id={labelId} className="nt-body-m-semibold text-[#0D110D]">
        {label}
      </span>
      <SelectPopover
        label={label}
        trigger={
          <button
            type="button"
            disabled={disabled}
            aria-labelledby={labelId}
            aria-describedby={`${labelId}-value`}
            className={cn(SELECT_FIELD, 'flex items-center justify-between gap-2 text-left')}
          >
            <span id={`${labelId}-value`} className="flex min-w-0 flex-1 items-center gap-2">
              {image ? <CmsIcon image={image} sizes="40px" className="h-5 w-10" /> : null}
              <span className="truncate">{value}</span>
            </span>
            <Icon name="caret-down" size={16} className="shrink-0" />
          </button>
        }
      >
        {children}
      </SelectPopover>
    </div>
  );
}

const normalizeChoice = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim();

export function SelectChoices<T extends string>({
  options,
  selected,
  label,
  placeholder,
  onChoose,
}: {
  options: { value: T; label: string; disabled?: boolean; image?: ImageType | null }[];
  selected: T;
  label: string;
  placeholder: string;
  onChoose: (value: T) => void;
}) {
  const [search, setSearch] = useState('');
  const id = useId();
  const list = useRef<HTMLUListElement>(null);
  const matches = options.filter(item =>
    normalizeChoice(item.label).includes(normalizeChoice(search)),
  );
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="search"
        autoComplete="off"
        placeholder={placeholder}
        value={search}
        onChange={event => {
          setSearch(event.target.value);
          list.current?.scrollTo({ top: 0 });
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const buttons =
              list.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
            const button = event.key === 'ArrowDown' ? buttons?.[0] : buttons?.[buttons.length - 1];
            button?.focus();
          } else if (event.key === 'Enter') {
            event.preventDefault();
            const enabled = matches.filter(item => !item.disabled);
            if (enabled.length === 1) onChoose(enabled[0].value);
          }
        }}
        className={cn(SELECT_FIELD, 'shrink-0')}
      />
      <p role="status" className="nt-body-s mb-1 mt-2 min-h-5 shrink-0 text-[#5D655D]">
        {matches.length
          ? 'Vyberte položku zo zoznamu.'
          : 'Žiadna položka nezodpovedá vyhľadávaniu.'}
      </p>
      <ul
        ref={list}
        onKeyDown={event => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const buttons = Array.from(
            event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'),
          );
          const index = buttons.indexOf(event.target as HTMLButtonElement);
          const next =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? buttons.length - 1
                : index + (event.key === 'ArrowDown' ? 1 : -1);
          buttons[(next + buttons.length) % buttons.length]?.focus();
        }}
        aria-label={label}
        style={{ height: Math.max(1, Math.min(options.length, 6)) * 44 }}
        className="min-h-0 overflow-y-auto overscroll-contain"
      >
        {matches.map(item => (
          <li key={item.value}>
            <button
              type="button"
              disabled={item.disabled}
              onClick={() => onChoose(item.value)}
              aria-current={item.value === selected ? 'true' : undefined}
              className="nt-body-m flex min-h-11 w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left hover:bg-[#F5F6F3] focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45"
            >
              <span className="flex min-w-0 flex-1 items-center gap-3">
                {item.image ? (
                  <CmsIcon image={item.image} sizes="48px" className="h-5 w-12" />
                ) : null}
                <span className="min-w-0 break-words">{item.label}</span>
              </span>
              {item.value === selected ? (
                <Icon name="check" size={16} className="shrink-0 text-[#127A26]" />
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
